import {
  ArrowRight,
} from 'lucide-react'

import {
  useRef,
  useState,
  type FormEvent,
} from 'react'

import {
  FormsConfigurationError,
  getRecaptchaSiteKey,
} from '../config/api'

import {
  describedBy,
  formLimits,
  type FormFieldErrors,
} from '../config/formLimits'

import {
  FORMS_SITE_NAME,
} from '../config/formsSite'

import {
  FormsApiError,
  submitInquiryForm,
} from '../services/formsApi'

import {
  trimmed,
} from '../utils/formValidation'

import {
  buildJoinInquiryMessage,
  joinExperienceOptions,
  joinMessageLimit,
  joinSpaceOptions,
  joinSpecialties,
  validateJoinInquiry,
} from '../utils/joinInquiryValidation'

import {
  CharacterCounter,
  FieldError,
} from './FormFieldFeedback'

import {
  Recaptcha,
} from './Recaptcha'

type Feedback =
  | {
      type:
        | 'success'
        | 'error'

      message:
        string
    }
  | null

export function JoinInquiryForm() {
  const [
    messageLength,
    setMessageLength,
  ] =
    useState(0)

  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(false)

  const [
    feedback,
    setFeedback,
  ] =
    useState<Feedback>(
      null,
    )

  const [
    fieldErrors,
    setFieldErrors,
  ] =
    useState<FormFieldErrors>(
      {},
    )

  const [
    captchaToken,
    setCaptchaToken,
  ] =
    useState('')

  const [
    captchaResetKey,
    setCaptchaResetKey,
  ] =
    useState(0)

  const submitLock =
    useRef(false)

  const recaptchaSiteKey =
    getRecaptchaSiteKey()

  const clearFieldError =
    (
      field:
        string,
    ) => {
      setFieldErrors(
        (current) => {
          if (
            !current[
              field
            ]
          ) {
            return current
          }

          const next = {
            ...current,
          }

          delete next[
            field
          ]

          return next
        },
      )
    }

  const submit =
    async (
      event:
        FormEvent<HTMLFormElement>,
    ) => {
      event.preventDefault()

      if (
        isSubmitting ||
        submitLock.current
      ) {
        return
      }

      const form =
        event.currentTarget

      const consentInput =
        form.elements.namedItem(
          'consent',
        ) as
          | HTMLInputElement
          | null

      if (
        !consentInput
          ?.checked
      ) {
        consentInput
          ?.focus()

        setFeedback({
          type:
            'error',

          message:
            'Please agree to the Privacy Policy and contact consent before submitting.',
        })

        return
      }

      const data =
        new FormData(
          form,
        )

      const name =
        trimmed(
          data.get(
            'name',
          ),
        )

      const email =
        trimmed(
          data.get(
            'email',
          ),
        ).toLowerCase()

      const rawPhone =
        trimmed(
          data.get(
            'phone',
          ),
        )

      const specialty =
        trimmed(
          data.get(
            'specialty',
          ),
        )

      const experience =
        trimmed(
          data.get(
            'experience',
          ),
        )

      const portfolioUrl =
        trimmed(
          data.get(
            'portfolioUrl',
          ),
        )

      const spaceInterest =
        trimmed(
          data.get(
            'spaceInterest',
          ),
        )

      const message =
        trimmed(
          data.get(
            'message',
          ),
        )

      const website =
        trimmed(
          data.get(
            'website',
          ),
        )

      const values = {
        name,
        email,
        rawPhone,
        specialty,
        experience,
        portfolioUrl,
        spaceInterest,
        message,
      }

      const {
        errors,
        phone,
      } =
        validateJoinInquiry(
          values,
          Boolean(
            recaptchaSiteKey,
          ),
        )

      if (
        recaptchaSiteKey &&
        !captchaToken
      ) {
        errors.recaptcha =
          'Please complete the security check.'
      }

      if (
        Object.keys(
          errors,
        ).length >
        0
      ) {
        setFieldErrors(
          errors,
        )

        setFeedback({
          type:
            'error',

          message:
            'Please review the marked fields and complete the security check.',
        })

        return
      }

      submitLock.current =
        true

      setIsSubmitting(
        true,
      )

      setFieldErrors(
        {},
      )

      setFeedback(
        null,
      )

      const inquiryMessage =
        buildJoinInquiryMessage(
          values,
        )

      try {
        await submitInquiryForm({
          formType:
            'inquiry',

          websiteName:
            FORMS_SITE_NAME,

          name,

          email,

          phone:
            phone ?? '',

          serviceType:
            'Other',

          otherServiceDescription:
            `${specialty} - ${spaceInterest}`,

          preferredContactMethod:
            'Email',

          message:
            inquiryMessage,

          pageUrl:
            window.location.href,

          submittedAt:
            new Date()
              .toISOString(),

          website,

          consent:
            consentInput.checked,

          recaptchaToken:
            captchaToken,
        })

        form.reset()

        setMessageLength(
          0,
        )

        setCaptchaToken(
          '',
        )

        setCaptchaResetKey(
          (value) =>
            value + 1,
        )

        setFeedback({
          type:
            'success',

          message:
            'Thank you. Your inquiry was sent securely, and the salon will follow up about the opportunity.',
        })
      } catch (error) {
        setCaptchaToken(
          '',
        )

        setCaptchaResetKey(
          (value) =>
            value + 1,
        )

        if (
          error instanceof
            FormsApiError ||
          error instanceof
            FormsConfigurationError
        ) {
          setFeedback({
            type:
              'error',

            message:
              error.userMessage,
          })

          setFieldErrors(
            error instanceof
              FormsApiError
              ? error.fieldErrors ??
                  {}
              : {},
          )
        } else {
          setFeedback({
            type:
              'error',

            message:
              'We could not send your inquiry. Please try again or contact the salon directly.',
          })
        }
      } finally {
        submitLock.current =
          false

        setIsSubmitting(
          false,
        )
      }
    }

  return (
    <form
      className="contact-form join-inquiry-form"
      onSubmit={submit}
      aria-busy={
        isSubmitting
      }
      noValidate
    >
      <p className="form-required-note">
        <span aria-hidden="true">
          *
        </span>{' '}
        Required fields
      </p>

      <div className="field-row">
        <label>
          <span className="field-label">
            Full name{' '}
            <span aria-hidden="true">
              *
            </span>
          </span>

          <input
            name="name"
            type="text"
            autoComplete="name"
            minLength={2}
            maxLength={
              formLimits.name
            }
            required
            aria-invalid={
              Boolean(
                fieldErrors.name,
              )
            }
            aria-describedby={
              fieldErrors.name
                ? 'join-name-error'
                : undefined
            }
            onChange={() =>
              clearFieldError(
                'name',
              )
            }
          />

          <FieldError
            id="join-name-error"
            message={
              fieldErrors.name
            }
          />
        </label>

        <label>
          <span className="field-label">
            Email{' '}
            <span aria-hidden="true">
              *
            </span>
          </span>

          <input
            name="email"
            type="email"
            autoComplete="email"
            maxLength={
              formLimits.email
            }
            required
            aria-invalid={
              Boolean(
                fieldErrors.email,
              )
            }
            aria-describedby={
              fieldErrors.email
                ? 'join-email-error'
                : undefined
            }
            onChange={() =>
              clearFieldError(
                'email',
              )
            }
          />

          <FieldError
            id="join-email-error"
            message={
              fieldErrors.email
            }
          />
        </label>
      </div>

      <div className="field-row">
        <label>
          <span className="field-label">
            Phone{' '}
            <span aria-hidden="true">
              *
            </span>
          </span>

          <input
            name="phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            maxLength={
              formLimits.phone
            }
            required
            aria-invalid={
              Boolean(
                fieldErrors.phone,
              )
            }
            aria-describedby={
              fieldErrors.phone
                ? 'join-phone-error'
                : undefined
            }
            onChange={() =>
              clearFieldError(
                'phone',
              )
            }
          />

          <FieldError
            id="join-phone-error"
            message={
              fieldErrors.phone
            }
          />
        </label>

        <label>
          <span className="field-label">
            Profession / specialty{' '}
            <span aria-hidden="true">
              *
            </span>
          </span>

          <select
            name="specialty"
            defaultValue=""
            required
            aria-invalid={
              Boolean(
                fieldErrors.specialty,
              )
            }
            aria-describedby={
              fieldErrors.specialty
                ? 'join-specialty-error'
                : undefined
            }
            onChange={() =>
              clearFieldError(
                'specialty',
              )
            }
          >
            <option value="">
              Select your specialty
            </option>

            {joinSpecialties.map(
              (
                option,
              ) => (
                <option
                  key={
                    option
                  }
                >
                  {
                    option
                  }
                </option>
              ),
            )}
          </select>

          <FieldError
            id="join-specialty-error"
            message={
              fieldErrors.specialty
            }
          />
        </label>
      </div>

      <div className="field-row">
        <label>
          <span className="field-label">
            Years of experience{' '}
            <span aria-hidden="true">
              *
            </span>
          </span>

          <select
            name="experience"
            defaultValue=""
            required
            aria-invalid={
              Boolean(
                fieldErrors.experience,
              )
            }
            aria-describedby={
              fieldErrors.experience
                ? 'join-experience-error'
                : undefined
            }
            onChange={() =>
              clearFieldError(
                'experience',
              )
            }
          >
            <option value="">
              Select experience
            </option>

            {joinExperienceOptions.map(
              (
                option,
              ) => (
                <option
                  key={
                    option
                  }
                >
                  {
                    option
                  }
                </option>
              ),
            )}
          </select>

          <FieldError
            id="join-experience-error"
            message={
              fieldErrors.experience
            }
          />
        </label>

        <label>
          <span className="field-label">
            Instagram / portfolio URL{' '}
            <span className="field-optional">
              Optional
            </span>
          </span>

          <input
            name="portfolioUrl"
            type="url"
            inputMode="url"
            placeholder="https://"
            maxLength={
              formLimits.otherServiceDescription
            }
            aria-invalid={
              Boolean(
                fieldErrors.portfolioUrl,
              )
            }
            aria-describedby={
              fieldErrors.portfolioUrl
                ? 'join-portfolio-error'
                : undefined
            }
            onChange={() =>
              clearFieldError(
                'portfolioUrl',
              )
            }
          />

          <FieldError
            id="join-portfolio-error"
            message={
              fieldErrors.portfolioUrl
            }
          />
        </label>
      </div>

      <label>
        <span className="field-label">
          What type of space interests you?{' '}
          <span aria-hidden="true">
            *
          </span>
        </span>

        <select
          name="spaceInterest"
          defaultValue=""
          required
          aria-invalid={
            Boolean(
              fieldErrors.spaceInterest,
            )
          }
          aria-describedby={
            fieldErrors.spaceInterest
              ? 'join-space-error'
              : undefined
          }
          onChange={() =>
            clearFieldError(
              'spaceInterest',
            )
          }
        >
          <option value="">
            Select a space type
          </option>

          {joinSpaceOptions.map(
            (
              option,
            ) => (
              <option
                key={
                  option
                }
              >
                {option}
              </option>
            ),
          )}
        </select>

        <FieldError
          id="join-space-error"
          message={
            fieldErrors.spaceInterest
          }
        />
      </label>

      <label>
        <span className="field-label">
          Tell us about yourself{' '}
          <span aria-hidden="true">
            *
          </span>{' '}
          <span className="field-optional">
            Add links above
          </span>
        </span>

        <textarea
          name="message"
          rows={6}
          minLength={10}
          maxLength={
            joinMessageLimit
          }
          required
          aria-invalid={
            Boolean(
              fieldErrors.message,
            )
          }
          aria-describedby={
            describedBy(
              'join-message-counter',

              fieldErrors.message &&
                'join-message-error',
            )
          }
          onChange={(
            event,
          ) => {
            setMessageLength(
              event.target
                .value.length,
            )

            clearFieldError(
              'message',
            )
          }}
        />

        <CharacterCounter
          id="join-message-counter"
          current={
            messageLength
          }
          maximum={
            joinMessageLimit
          }
        />

        <FieldError
          id="join-message-error"
          message={
            fieldErrors.message
          }
        />
      </label>

      <label
        className="honeypot-field"
        aria-hidden="true"
      >
        Leave this field empty

        <input
          name="website"
          type="text"
          maxLength={200}
          tabIndex={-1}
          autoComplete="off"
        />
      </label>

      <Recaptcha
        onTokenChange={(
          token,
        ) => {
          setCaptchaToken(
            token,
          )

          if (token) {
            clearFieldError(
              'recaptcha',
            )
          }
        }}
        resetKey={
          captchaResetKey
        }
        theme="light"
      />

      <FieldError
        id="join-recaptcha-error"
        message={
          fieldErrors.recaptcha
        }
      />

      <label className="form-consent">
        <input
          name="consent"
          type="checkbox"
          required
          aria-required="true"
        />

        <span>
          I agree to the{' '}
          <a href="/privacy-policy">
            Privacy Policy
          </a>{' '}
          and consent to being contacted regarding this inquiry.
        </span>
      </label>

      <button
        className="button"
        type="submit"
        disabled={
          isSubmitting ||
          !recaptchaSiteKey
        }
        aria-disabled={
          isSubmitting ||
          !recaptchaSiteKey
        }
      >
        {isSubmitting
          ? 'Sending...'
          : (
            <>
              Start a conversation{' '}
              <ArrowRight />
            </>
          )}
      </button>

      <div
        className={`form-note${
          feedback
            ? ` form-note--${feedback.type}`
            : ''
        }`}
        role={
          feedback?.type ===
          'error'
            ? 'alert'
            : 'status'
        }
        aria-live="polite"
        aria-atomic="true"
      >
        {feedback?.message}
      </div>
    </form>
  )
}